import React, { useState, useEffect, useCallback } from 'react';
import { Table, Row, Col, DatePicker, Input, Button, Space, Select, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { academySubscriptionApi } from '@/services/api';
import { formatNumber } from '@/utils/number';

interface PaymentData {
  key: string;
  no: number;
  paymentStatus: string;
  paymentType: string;
  amount: number;
  transactionDate: string;
}

interface PaymentHistoryProps {
  academyCode: string;
}

const paymentStatusMeta: Record<string, { label: string; color: string }> = {
  partial_refund: { label: '부분환불', color: '#ea580c' }, // orange-600
  '부분환불': { label: '부분환불', color: '#ea580c' },
  cancel: { label: '취소', color: '#dc2626' }, // red-600
  cancelled: { label: '취소', color: '#dc2626' },
  '취소': { label: '취소', color: '#dc2626' },
  fail: { label: '결제실패', color: '#ef4444' }, // red-500
  failed: { label: '결제실패', color: '#ef4444' },
  '실패': { label: '결제실패', color: '#ef4444' },
  pending: { label: '대기', color: '#ca8a04' }, // yellow-600
  '대기': { label: '대기', color: '#ca8a04' },
  '결제': { label: '결제', color: '#059669' }, // emerald-600
};

const renderPaymentStatus = (status: string) => {
  const normalized = (status || '').trim().toLowerCase();
  const meta =
    paymentStatusMeta[status] ||
    paymentStatusMeta[normalized] ||
    paymentStatusMeta['결제'];

  return (
    <span style={{ color: meta.color, fontWeight: 600 }}>
      {meta.label}
    </span>
  );
};

type PaymentHistoryOverrides = {
  searchText?: string;
  startDate?: dayjs.Dayjs | null;
};

const PaymentHistory: React.FC<PaymentHistoryProps> = ({ academyCode }) => {
  const [payments, setPayments] = useState<PaymentData[]>([]);
  const [filteredPayments, setFilteredPayments] = useState<PaymentData[]>([]);
  const [searchText, setSearchText] = useState('');
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
  });

  const applyCategoryFilter = useCallback(
    (items: PaymentData[]) => {
      if (selectedCategory && selectedCategory !== '') {
        return items.filter((payment) => payment.paymentStatus === selectedCategory);
      }
      return items;
    },
    [selectedCategory],
  );

  const fetchPaymentHistory = useCallback(
    async (page = 1, pageSizeOverride?: number, overrides?: PaymentHistoryOverrides) => {
      if (!academyCode) {
        setPayments([]);
        setFilteredPayments([]);
        return;
      }

      const resolvedPageSize = pageSizeOverride || pagination.pageSize;
      const searchValue = overrides?.searchText ?? searchText;
      const startDateValue = overrides?.startDate ?? startDate;

      setLoading(true);
      try {
        const res = await academySubscriptionApi.paymentHistory({
          academyCode,
          search: searchValue ? searchValue : undefined,
          startDate: startDateValue ? startDateValue.format('YYYY-MM-DD') : undefined,
          page,
          pageSize: resolvedPageSize,
        });

        const body = res?.data || res || {};
        const items = body.items || [];
        const totalCount = body.total ?? items.length;

        const mapped: PaymentData[] = items.map((item: any, index: number) => ({
          key: item.id || `${page}-${index}`,
          no: totalCount > 0 ? totalCount - ((page - 1) * resolvedPageSize + index) : items.length - index,
          paymentStatus: item.paymentStatus || '결제',
          paymentType: item.paymentType || '-',
          amount: Number(item.amount || 0),
          transactionDate: item.transactionDate || '-',
        }));

        setPayments(mapped);
        setFilteredPayments(applyCategoryFilter(mapped));
        setPagination({
          total: body.total ?? mapped.length,
          page: body.page ?? page,
          pageSize: body.pageSize ?? resolvedPageSize,
          totalPages: body.totalPages ?? Math.ceil((body.total ?? mapped.length) / resolvedPageSize),
        });
      } catch (error) {
        console.error('[Admin PaymentHistory] 결제 이력 조회 실패:', error);
        message.error('결제 이력을 불러오지 못했습니다.');
        setPayments([]);
        setFilteredPayments([]);
      } finally {
        setLoading(false);
      }
    },
    [academyCode, applyCategoryFilter, pagination.pageSize, searchText, startDate],
  );

  useEffect(() => {
    fetchPaymentHistory(1);
  }, [academyCode, fetchPaymentHistory]);

  useEffect(() => {
    setFilteredPayments(applyCategoryFilter(payments));
  }, [applyCategoryFilter, payments]);

  const handleSearch = () => {
    fetchPaymentHistory(1);
  };

  const handleReset = () => {
    setSearchText('');
    setStartDate(null);
    setSelectedCategory(undefined); 
    fetchPaymentHistory(1, undefined, { searchText: '', startDate: null });
  };

  const handleTableChange = (tablePagination: any) => {
    const { current = 1, pageSize = pagination.pageSize } = tablePagination || {};
    fetchPaymentHistory(current, pageSize);
  };

  const paymentColumns: ColumnsType<PaymentData> = [
    { title: '순번', dataIndex: 'no', key: 'no', width: 80, align: 'center', render: (no: number) => formatNumber(no) },
    { 
      title: '구분', 
      dataIndex: 'paymentStatus', 
      key: 'paymentStatus', 
      width: 120,
      render: (value: string) => renderPaymentStatus(value),
    },
    { title: '결제유형', dataIndex: 'paymentType', key: 'paymentType', width: 120 },
    { 
      title: '결제금액', 
      dataIndex: 'amount', 
      key: 'amount', 
      width: 120,
      render: (amount: number) => `${formatNumber(amount)}원`,
    },
    { title: '거래일', dataIndex: 'transactionDate', key: 'transactionDate', width: 120 },
  ];

  return (
    <div>
      {/* 검색 영역 */}
      <Row gutter={16} align="middle" style={{ marginBottom: 16 }}>
        <Col span={4}>
          <Select
            value={selectedCategory}
            onChange={setSelectedCategory}
            style={{ width: '100%' }}
            placeholder="구분"
            allowClear
          >
            <Select.Option value="결제">결제</Select.Option>
            <Select.Option value="실패">실패</Select.Option>
            <Select.Option value="취소">취소</Select.Option>
          </Select>
        </Col>
        <Col span={4}>
          <DatePicker
            value={startDate}
            onChange={(date) => setStartDate(date)}
            style={{ width: '100%' }}
            placeholder="거래일"
          />
        </Col>

        <Col span={4}>
          <Input
            placeholder="검색"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onPressEnter={handleSearch}
            prefix={<SearchOutlined />}
          />
        </Col>
        <Col span={4}>
          <Space>
            <Button type="primary" onClick={handleSearch} icon={<SearchOutlined />}>
              검색
            </Button>
            <Button onClick={handleReset}>
              초기화
            </Button>
          </Space>
        </Col>
      </Row>

      {/* 테이블 */}
      <Table
        columns={paymentColumns}
        dataSource={filteredPayments}
        loading={loading}
        pagination={{
          current: pagination.page,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => {
            if (!total) return `0-0 / 총 ${formatNumber(0)}개`;
            const start = Math.max(total - range[1] + 1, 1);
            const end = Math.max(total - range[0] + 1, 1);
            return `${formatNumber(start)}-${formatNumber(end)} / 총 ${formatNumber(total)}개`;
          },
        }}
        onChange={handleTableChange}
        scroll={{ x: 600 }}
      />
    </div>
  );
};

export default PaymentHistory; 